/**
 * Auth Guards for Route Protection
 *
 * These guards protect routes based on authentication state and user roles.
 * They integrate with AuthService signals for reactive auth state.
 */
import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Wait for auth state to finish loading
 */
function waitForAuthLoad(authService: AuthService): Promise<boolean> {
  return new Promise((resolve) => {
    // If already loaded, resolve immediately
    if (!authService.isLoading()) {
      resolve(authService.isAuthenticated());
      return;
    }

    // Otherwise, wait for isLoading to become false
    const checkInterval = setInterval(() => {
      if (!authService.isLoading()) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve(authService.isAuthenticated());
      }
    }, 50); // Check every 50ms

    // Timeout after 5 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
      console.warn('[authGuard] Auth loading timeout - assuming not authenticated');
      resolve(false);
    }, 5000);
  });
}

/**
 * Guard to protect routes requiring authentication.
 * Redirects to /login if user is not authenticated.
 * Waits for auth state to load before making decision.
 */
export const authGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth to finish loading
  const isAuthenticated = await waitForAuthLoad(authService);

  if (isAuthenticated) {
    console.log('[authGuard] User authenticated, allowing access');
    return true;
  }

  // Not authenticated - redirect to login
  console.log('[authGuard] Not authenticated, redirecting to /login');
  return router.createUrlTree(['/login']);
};

/**
 * Guard to prevent authenticated users from accessing login page.
 * Redirects to /dashboard if user is already authenticated.
 */
export const noAuthGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth to finish loading
  const isAuthenticated = await waitForAuthLoad(authService);

  // If authenticated, redirect to dashboard
  if (isAuthenticated) {
    console.log('[noAuthGuard] Already authenticated, redirecting to /dashboard');
    return router.createUrlTree(['/dashboard']);
  }

  // Not authenticated - allow access to login page
  return true;
};

/**
 * Guard for client admin only routes.
 * Requires authentication AND clientAdmin role.
 * Note: For now, everybody sees everything except user creation flow.
 */
export const clientAdminGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth to finish loading
  const isAuthenticated = await waitForAuthLoad(authService);

  // Check authentication first
  if (!isAuthenticated) {
    console.log('[clientAdminGuard] Not authenticated, redirecting to /login');
    return router.createUrlTree(['/login']);
  }

  // Check clientAdmin role
  if (authService.isClientAdmin()) {
    return true;
  }

  // Authenticated but not clientAdmin - redirect to dashboard
  console.warn('[clientAdminGuard] Access denied - requires clientAdmin role');
  // TODO: Show toast notification "Access denied - requires administrator privileges"
  return router.createUrlTree(['/dashboard']);
};
